export interface OrgDefaultRate {
  roleId: string;
  roleName: string;
  hourlyCostRate: number;
  hourlyBillRate: number;
  dailyCostRate: number;
  dailyBillRate: number;
}

export interface OrgPerson {
  personId: string;
  personName: string;
  roleId: string;
  hourlyCostRate?: number;
  hourlyBillRate?: number;
  dailyCostRate?: number;
  dailyBillRate?: number;
}

// Mock organization default rates (from Resource Manager)
const ORG_DEFAULT_RATES: OrgDefaultRate[] = [
  {
    roleId: 'role-1',
    roleName: 'Project Manager',
    hourlyCostRate: 90,
    hourlyBillRate: 150,
    dailyCostRate: 720,
    dailyBillRate: 1200,
  },
  {
    roleId: 'role-2',
    roleName: 'Senior Consultant',
    hourlyCostRate: 75,
    hourlyBillRate: 125,
    dailyCostRate: 600,
    dailyBillRate: 1000,
  },
  {
    roleId: 'role-3',
    roleName: 'Consultant',
    hourlyCostRate: 60,
    hourlyBillRate: 100,
    dailyCostRate: 480,
    dailyBillRate: 800,
  },
  {
    roleId: 'role-4',
    roleName: 'Junior Consultant',
    hourlyCostRate: 45,
    hourlyBillRate: 75,
    dailyCostRate: 360,
    dailyBillRate: 600,
  },
  {
    roleId: 'role-5',
    roleName: 'Technical Architect',
    hourlyCostRate: 100,
    hourlyBillRate: 175,
    dailyCostRate: 800,
    dailyBillRate: 1400,
  },
  {
    roleId: 'role-6',
    roleName: 'Business Analyst',
    hourlyCostRate: 65,
    hourlyBillRate: 110,
    dailyCostRate: 520,
    dailyBillRate: 880,
  },
  {
    roleId: 'role-7',
    roleName: 'Developer',
    hourlyCostRate: 55,
    hourlyBillRate: 95,
    dailyCostRate: 440,
    dailyBillRate: 760,
  },
  {
    roleId: 'role-8',
    roleName: 'QA Tester',
    hourlyCostRate: 50,
    hourlyBillRate: 85,
    dailyCostRate: 400,
    dailyBillRate: 680,
  },
  {
    roleId: 'role-9',
    roleName: 'Senior QA',
    hourlyCostRate: 59.38,
    hourlyBillRate: 118.75,
    dailyCostRate: 475.04,
    dailyBillRate: 950,
  },
];

// Mock people assigned to roles
const ORG_PEOPLE: OrgPerson[] = [
  // Project Manager
  { personId: '1', personName: 'Sarah Johnson', roleId: 'role-1' },
  // Senior Consultants
  { personId: '2', personName: 'Michael Chen', roleId: 'role-2' },
  { personId: '3', personName: 'Emily Rodriguez', roleId: 'role-2' },
  // Developers
  { personId: '4', personName: 'David Kim', roleId: 'role-3' },
  { personId: '5', personName: 'Jessica Lee', roleId: 'role-3' },
  // QA Analyst
  { personId: '6', personName: 'Alex Turner', roleId: 'role-4' },
  // Additional people for other roles
  { personId: '7', personName: 'Person E', roleId: 'role-5' },
  { personId: '8', personName: 'Person F', roleId: 'role-6' },
  { personId: '9', personName: 'Person G', roleId: 'role-7' },
  { personId: '10', personName: 'Person H', roleId: 'role-8' },
];

export class MockOrgDefaultsService {
  /**
   * Get organization default rates for all roles
   * In production, this would call Resource Manager API
   */
  static getOrgDefaultRates(): OrgDefaultRate[] {
    return ORG_DEFAULT_RATES;
  }

  /**
   * Get organization default rate for a specific role
   */
  static getOrgDefaultRateByRole(roleId: string): OrgDefaultRate | undefined {
    return ORG_DEFAULT_RATES.find(r => r.roleId === roleId);
  }

  /**
   * Get all people in the organization
   * In production, this would call Resource Manager API
   */
  static getAllPeople(): OrgPerson[] {
    return ORG_PEOPLE;
  }

  /**
   * Get people assigned to a specific role
   */
  static getPeopleByRole(roleId: string): OrgPerson[] {
    return ORG_PEOPLE.filter(p => p.roleId === roleId);
  }

  /**
   * Get a specific person by ID
   */
  static getPersonById(personId: string): OrgPerson | undefined {
    return ORG_PEOPLE.find(p => p.personId === personId);
  }
}
